import { ref, computed, watch } from 'vue';

/**
 * BarChart / Distribution (v3 homepage) — core logic, shared by both portals.
 *
 * Owns the data + cascading-filter state + fetching for the design's vertical
 * distribution chart (static rows OR api via rowMapping). `self` pattern;
 * caller injects an axios-like `http` (the `documentApi(http)` pattern) and a
 * `getConfig` getter for the widget's settings. The filter DROPDOWN rendering
 * differs per portal (beta Teleport, legacy v-menu) and stays in presentation —
 * both call the same pick/clear/optionsFor/filterLabel/isSelected methods here.
 *
 * @param {Object} options
 * @param {{get:Function,post:Function}} options.http
 * @param {() => Object} options.getConfig  widget config (title, dataSource, apiEndpoint, apiMethod, apiParams, rowMapping, rows, filters, barColor, categoryLabel, linkText, linkUrl)
 * @param {() => boolean} [options.getEditMode] skip fetching in editor preview
 * @param {(err:any) => void} [options.onError] e.g. surface session-expired
 * @param {() => void} [options.onReady] called once after the first resolve
 */
export function barChart(options) {
  const {
    http,
    getConfig = () => ({}),
    getEditMode = () => false,
    onError = () => {},
    onReady = () => {},
  } = options || {};

  const cfg = () => getConfig() || {};

  const self = {
    filterValues: ref({}),
    apiRows: ref([]),
    filterOptions: ref({}),
    persistedFilterOptions: ref({}),
    loading: ref(false),
  };

  self.barColour = computed(() => cfg().barColor || 'var(--v3-accent)');
  self.categoryLabel = computed(() => cfg().categoryLabel || '');
  self.linkText = computed(() => cfg().linkText || '');
  self.resolvedFilters = computed(() => (Array.isArray(cfg().filters) ? cfg().filters : []));
  self.resolvedRows = computed(() => {
    if (cfg().dataSource === 'api') return self.apiRows.value;
    return Array.isArray(cfg().rows) ? cfg().rows : [];
  });
  self.maxValue = computed(() => Math.max(1, ...self.resolvedRows.value.map((r) => Number(r.value) || 0)));
  self.total = computed(() => self.resolvedRows.value.reduce((s, r) => s + (Number(r.value) || 0), 0));
  self.peak = computed(() =>
    self.resolvedRows.value.reduce(
      (a, b) => ((Number(b.value) || 0) > (Number(a.value) || 0) ? b : a),
      self.resolvedRows.value[0] || { label: '', value: 0 },
    ),
  );
  self.barH = (v) => ((Number(v) || 0) / self.maxValue.value) * 100 + '%';
  // Peak-insight callout (prototype V3Distribution): "Peaks at Level 4 (41)".
  self.peakInsight = computed(() => {
    const p = self.peak.value;
    if (!p || (!p.label && !p.value)) return '';
    const cat = self.categoryLabel.value;
    return `Peaks at ${cat ? cat + ' ' : ''}${p.label} (${Number(p.value) || 0})`;
  });

  // --- filter option helpers ---
  self.optVal = (o) => (o && typeof o === 'object' ? o.value : o);
  self.optText = (o) => (o && typeof o === 'object' ? o.text : String(o));
  self.isSelected = (f, o) => {
    const v = self.filterValues.value[f.key];
    return Array.isArray(v) && v.includes(self.optVal(o));
  };
  self.optionsFor = (f) => {
    if (!f) return [];
    if (f.allItemsField && self.persistedFilterOptions.value[f.key]?.length) return self.persistedFilterOptions.value[f.key];
    if (f.optionsEndpoint && self.filterOptions.value[f.key]) return self.filterOptions.value[f.key];
    return f.options || [];
  };
  self.filterLabel = (f) => {
    const v = self.filterValues.value[f.key];
    if (f.multiple) return f.label + (Array.isArray(v) && v.length ? ` (${v.length})` : '');
    if (v === null || v === undefined || v === '') return f.label;
    const opt = (self.optionsFor(f) || []).find((o) => self.optVal(o) === v);
    return opt ? self.optText(opt) : f.label;
  };
  self.clearFilter = (f) => {
    self.filterValues.value = { ...self.filterValues.value, [f.key]: f.multiple ? [] : null };
  };
  self.pickFilter = (f, valOrOpt) => {
    const val = typeof valOrOpt === 'object' && valOrOpt !== null && 'value' in valOrOpt ? valOrOpt.value : valOrOpt;
    const next = { ...self.filterValues.value };
    if (f.multiple) {
      const cur = Array.isArray(next[f.key]) ? [...next[f.key]] : [];
      const i = cur.indexOf(val);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(val);
      next[f.key] = cur;
    } else {
      next[f.key] = val;
    }
    self.filterValues.value = next;
  };

  function buildParams() {
    const out = {};
    (cfg().apiParams || []).forEach((p) => {
      if (p && p.name) out[p.name] = p.value;
    });
    const fv = self.filterValues.value || {};
    Object.keys(fv).forEach((k) => {
      const v = fv[k];
      if (Array.isArray(v)) {
        if (v.length) out[k] = v;
        return;
      }
      if (v !== null && v !== undefined && v !== '') out[k] = v;
    });
    return out;
  }

  function mapRows(raw) {
    if (!Array.isArray(raw)) return [];
    const m = cfg().rowMapping || {};
    const lf = m.labelField || 'label';
    const vf = m.valueField || 'value';
    return raw.map((r) => ({ label: r[lf], value: Number(r[vf]) || 0 }));
  }

  self.fetchData = async () => {
    if (getEditMode()) return;
    const endpoint = cfg().apiEndpoint;
    if (!endpoint) return;
    const method = (cfg().apiMethod || 'POST').toUpperCase();
    const params = buildParams();
    self.loading.value = true;
    try {
      const resp = method === 'GET' ? await http.get(endpoint, { params }) : await http.post(endpoint, params);
      const data = resp.data;
      let raw = [];
      if (Array.isArray(data)) raw = data;
      else if (data && data.Status === 'OK') {
        if (Array.isArray(data.Data)) raw = data.Data;
        else if (data.Data && Array.isArray(data.Data.rows)) {
          raw = data.Data.rows;
          self.resolvedFilters.value.forEach((f) => {
            if (f.allItemsField && Array.isArray(data.Data[f.allItemsField])) {
              self.persistedFilterOptions.value = {
                ...self.persistedFilterOptions.value,
                [f.key]: data.Data[f.allItemsField].map((item) =>
                  typeof item === 'string' || typeof item === 'number' ? { text: String(item), value: item } : item,
                ),
              };
            }
          });
        }
      }
      self.apiRows.value = mapRows(raw);
    } catch (err) {
      self.apiRows.value = [];
      onError(err);
    } finally {
      self.loading.value = false;
    }
  };

  self.fetchFilterOptions = async (f) => {
    if (getEditMode() || !f || !f.optionsEndpoint) return;
    const body = {};
    (f.optionsParams || []).forEach((p) => {
      if (p && p.name) body[p.name] = p.value;
    });
    const fv = self.filterValues.value || {};
    Object.keys(fv).forEach((k) => {
      if (k === f.key) return;
      const v = fv[k];
      if (v !== null && v !== undefined && v !== '') body[k] = v;
    });
    try {
      const method = (f.optionsMethod || 'GET').toUpperCase();
      const resp = method === 'POST' ? await http.post(f.optionsEndpoint, body) : await http.get(f.optionsEndpoint, { params: body });
      const raw = Array.isArray(resp.data) ? resp.data : resp.data && resp.data.Status === 'OK' ? resp.data.Data : null;
      const m = f.optionsMapping || {};
      const lf = m.labelField || 'label';
      const vf = m.valueField || 'value';
      self.filterOptions.value = {
        ...self.filterOptions.value,
        [f.key]: Array.isArray(raw)
          ? raw.map((item) =>
              typeof item === 'string' || typeof item === 'number'
                ? { text: String(item), value: item }
                : { text: item[lf] ?? item[vf] ?? '', value: item[vf] ?? item[lf] ?? '' },
            )
          : [],
      };
    } catch (err) {
      onError(err);
    }
  };

  // Seed filter defaults.
  self.resolvedFilters.value.forEach((f) => {
    if (self.filterValues.value[f.key] === undefined) {
      self.filterValues.value = { ...self.filterValues.value, [f.key]: f.default ?? (f.multiple ? [] : null) };
    }
  });

  // Initial + reactive (re)fetch on config/filter change.
  let didReady = false;
  const fetchKey = computed(() =>
    JSON.stringify({ ds: cfg().dataSource, ep: cfg().apiEndpoint, m: cfg().apiMethod, p: cfg().apiParams, f: self.filterValues.value }),
  );
  watch(
    fetchKey,
    async () => {
      if (cfg().dataSource === 'api' && cfg().apiEndpoint) await self.fetchData();
      else self.apiRows.value = [];
      self.resolvedFilters.value.forEach((f) => {
        if (f.optionsEndpoint) self.fetchFilterOptions(f);
      });
      if (!didReady) {
        didReady = true;
        onReady();
      }
    },
    { immediate: true },
  );

  return self;
}
